/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import { useQuery } from '@apollo/react-hooks';
import { useCallback } from 'react';

import { getMonitoringClient } from '@confluence/monitoring';
import { Attribution, isUnauthorizedError } from '@confluence/error-boundary';
import { fg } from '@confluence/feature-gating';
import { markErrorAsHandled } from '@confluence/graphql';

import type {
	useSpaceManagementOperationsSpacePropertiesQuery as useSpaceManagementOperationsSpacePropertiesQuery$data,
	useSpaceManagementOperationsSpacePropertiesQueryVariables as useSpaceManagementOperationsSpacePropertiesQuery$variables,
} from './__types__/useSpaceManagementOperationsSpacePropertiesQuery';

const useSpaceManagementOperationsSpacePropertiesQuery = gql`
	query useSpaceManagementOperationsSpacePropertiesQuery($spaceKey: String!) {
		space(key: $spaceKey) {
			id
			currentUser {
				isAdmin
			}
			operations {
				operation
				targetType
			}
		}
	}
`;
interface SpaceManagementOperations {
	canManageSomeUsers: boolean;
	canManageUsers: boolean;
	canManageNonlicensedUsers: boolean;
	canManageGuestUsers: boolean;
	canManagePublicLinks: boolean;
	canAdminister: boolean;
	loading: boolean;
}

export const MANAGE_USERS = 'manage_users';
export const MANAGE_NONLICENSED_USERS = 'manage_nonlicensed_users';
export const MANAGE_GUEST_USERS = 'manage_guest_users';
export const MANAGE_PUBLIC_LINKS = 'manage_public_links';
export const ADMINISTER = 'administer';

/**
 * Hook to check if the user has the required operations to manage users in a space.
 */
export const useSpaceManagementOperations = ({
	spaceKey,
}: {
	spaceKey: string;
}): SpaceManagementOperations => {
	const { data, loading } = useQuery<
		useSpaceManagementOperationsSpacePropertiesQuery$data,
		useSpaceManagementOperationsSpacePropertiesQuery$variables
	>(useSpaceManagementOperationsSpacePropertiesQuery, {
		variables: { spaceKey },
		skip: !spaceKey || !fg('cc_perms_exp_rbac_fe_milestone_2'),
		onError: (error) => {
			if (isUnauthorizedError(error)) {
				markErrorAsHandled(error);
			} else {
				getMonitoringClient().submitError(error, {
					attribution: Attribution.PERMISSIONS_EXPERIENCE,
				});
			}
		},
	});
	const hasSpaceOperation = useCallback(
		(
			spaceOperation:
				| typeof MANAGE_USERS
				| typeof MANAGE_NONLICENSED_USERS
				| typeof MANAGE_GUEST_USERS
				| typeof MANAGE_PUBLIC_LINKS
				| typeof ADMINISTER,
		) => {
			return (
				data?.space?.operations?.some(
					(operation) =>
						operation?.operation === spaceOperation && operation?.targetType === 'space',
				) ?? false
			);
		},
		[data],
	);

	return {
		canManageSomeUsers:
			hasSpaceOperation(MANAGE_USERS) ||
			hasSpaceOperation(MANAGE_NONLICENSED_USERS) ||
			hasSpaceOperation(MANAGE_GUEST_USERS),
		canManageUsers: hasSpaceOperation(MANAGE_USERS),
		canManageNonlicensedUsers: hasSpaceOperation(MANAGE_NONLICENSED_USERS),
		canManageGuestUsers: hasSpaceOperation(MANAGE_GUEST_USERS),
		canManagePublicLinks: hasSpaceOperation(MANAGE_PUBLIC_LINKS),
		canAdminister: hasSpaceOperation(ADMINISTER),
		loading,
	};
};
